import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { DownloadSimple, ShieldWarning } from "@phosphor-icons/react";

export function PrivacyPane() {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");

  const exportData = useMutation({
    mutationFn: () => api.get("/privacy/export"),
    onSuccess: ({ data }) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `my-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded");
    },
    onError: (e) => toast.error(errText(e)),
  });

  const deleteReq = useMutation({
    mutationFn: () => api.post("/privacy/delete-request", { reason }),
    onSuccess: ({ data }) => { toast.success(data.message); setConfirming(false); setReason(""); },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="privacy-pane">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><ShieldWarning size={15} /> Your privacy &amp; data rights</h3>
      <p className="text-[11px] text-zinc-500 mb-4">Under GDPR &amp; CCPA you can download everything we hold about you, or request deletion.</p>
      <div className="flex flex-col gap-2">
        <Button data-testid="privacy-export-btn" size="sm" variant="outline" className="justify-start" disabled={exportData.isPending} onClick={() => exportData.mutate()}>
          <DownloadSimple size={14} className="mr-1.5" /> {exportData.isPending ? "Preparing…" : "Download all my data"}
        </Button>
        <Button data-testid="consent-pdf-btn" size="sm" variant="outline" className="justify-start" onClick={async () => {
          try {
            const r = await api.get("/portal/consent/pdf", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = url; a.download = "artha-7216-consent.pdf"; a.click(); URL.revokeObjectURL(url);
          } catch { toast.error("No signed consent on file yet"); }
        }}>
          <DownloadSimple size={14} className="mr-1.5" /> My signed § 7216 consent (PDF)
        </Button>
        {confirming ? (
          <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3" data-testid="privacy-delete-form">
            <p className="text-[11px] text-rose-800 mb-2">Request deletion of your personal data. The firm reviews within 30 days; some financial records must be retained by law.</p>
            <Textarea data-testid="privacy-delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="text-xs min-h-[50px] mb-2" />
            <div className="flex gap-2">
              <Button data-testid="privacy-delete-confirm-btn" size="sm" variant="destructive" className="h-7 text-[11px]" disabled={deleteReq.isPending} onClick={() => deleteReq.mutate()}>Submit request</Button>
              <Button data-testid="privacy-delete-cancel-btn" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <button data-testid="privacy-delete-btn" onClick={() => setConfirming(true)} className="text-[11px] text-zinc-400 underline hover:text-rose-600 transition-colors duration-150 text-left">Request data deletion</button>
        )}
      </div>
    </div>
  );
}
